import React, { useEffect, useRef, useState } from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { shareIcon } from "../svgs";
import dayjs from "dayjs";
import api from "../../../api/api";
import { Skeleton } from "@mui/material";
import { getPracticeId, practiceFeatures } from "../../../config/practice";
import { postCallNote } from "../../../fax/chat/callNotes";

export default function MessagePatient({ data }) {
  const practiseid = getPracticeId();
  const listRef = useRef(null);

  const [chatdata, setChatData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inputText, setInputText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [chatloader, setChatLoader] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [previewUrls, setPreviewUrls] = useState({});
  const [objectUrl, setObjectUrl] = useState("");
  const [lastSentAt, setLastSentAt] = useState(null);   // enables Unsend for 90s
  const [unsendBusy, setUnsendBusy] = useState(false);
  const fetchchat = async (newOffset = 0, append = false) => {
    if (loadingMore) return;

    setLoadingMore(true);

    try {
      const response = await api.post(
        `/v1/${practiseid}/messaging/chatdetails`,
        {
          patientid: data.patientid,
          limit: 50,
          offset: newOffset,
        }
      );

      if (response.status === 200) {
        const messages = response.data.reverse();

        if (append) {
          const scrollContainer = listRef.current;
          const previousScrollHeight = scrollContainer.scrollHeight;

          setChatData((prev) => [...messages, ...prev]);

          // After data updates and renders, restore scroll position
          setTimeout(() => {
            const newScrollHeight = scrollContainer.scrollHeight;
            scrollContainer.scrollTop = newScrollHeight - previousScrollHeight;
          }, 100);
        } else {
          setChatData(messages);

          // Scroll to bottom on initial load
          setTimeout(() => {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
          }, 100);

          setInitialLoad(false);
        }

        if (messages.length < 50) {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.log("Error fetching chat:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchchat();
  }, []);
  function AttachmentPreview({ attachment }) {
    const [objectUrl, setObjectUrl] = useState("");

    useEffect(() => {
      const url = URL.createObjectURL(attachment);
      setObjectUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }, [attachment]);

    if (attachment.type?.startsWith("image/")) {
      return (
        <img
          src={objectUrl}
          alt="Preview"
          className="h-32 w-32 object-cover rounded-lg mb-2"
        />
      );
    }

    if (attachment.type === "application/pdf") {
      return (
        <a
          href={objectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline mb-2"
        >
          View PDF: {attachment.name}
        </a>
      );
    }

    if (
      attachment.type === "audio/mpeg" ||
      attachment.type === "audio/wav" ||
      attachment.type === "audio/mp3"
    ) {
      return (
        <audio controls className="w-full mb-2">
          <source src={objectUrl} type={attachment.type} />
          Your browser does not support the audio element.
        </audio>
      );
    }

    return (
      <div className="text-sm mb-2">
        <a
          href={objectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline"
        >
          {attachment.name}
        </a>
      </div>
    );
  }

  const handleScroll = () => {
    if (listRef.current.scrollTop === 0 && hasMore && !loadingMore) {
      const newOffset = offset + 50;
      fetchchat(newOffset, true);
      setOffset(newOffset);
    }
  };
  useEffect(() => {
    // Fetch previews for all attachments that are not yet fetched
    [...chatdata].forEach((message) => {
      if (message.attachement && !previewUrls[message.attachement]) {
        fetchDocument(message.attachement);
      }
    });
  }, [chatdata]);
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // A patient switch must clear EVERYTHING composed for the previous patient
  // — typed text + attached image survived the switch, so a pending message
  // could be sent to the next patient (QA 2026-08-28).
  useEffect(() => {
    setInputText("");
    setAttachment(null);
    setChatLoader(false);
    setLastSentAt(null);
  }, [data?.patientid]);

  const sendMessage = async () => {
    if (!inputText.trim() && !attachment) return;
    if (chatloader) return; // in-flight — no double sends
    // Bind the recipient NOW: if the selection changes while a slow image
    // upload is in flight, the message must still go to the patient it was
    // written for (QA 2026-08-28).
    const targetPid = data.patientid;

    setChatLoader(true);
    try {
      const loginUser = localStorage.getItem("user");
      const payload = {
        source: "practice",
        message: inputText,
        loginuser: loginUser,
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      if (attachment) formData.append("attachment", attachment);

      if (practiceFeatures().callNotes) {
        // Call notes (7825): v1 send-message, never schat (fax/chat/callNotes.js).
        await postCallNote(practiseid, targetPid, inputText, loginUser);
      } else {
      await api.post(
        `/v1/${practiseid}/messaging/patient/${targetPid}/schat`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      }

      setInputText("");
      setAttachment(null);
      setOffset(0);
      setHasMore(true);
      setLastSentAt(Date.now());
      if (targetPid === data.patientid) fetchchat();
    } catch (err) {
      console.log(err);
    } finally {
      setChatLoader(false);
    }
  };

  const unsendLast = async () => {
    if (unsendBusy) return;
    setUnsendBusy(true);
    try {
      const res = await api.post(
        `/v1/${practiseid}/messaging/patient/${data.patientid}/schat/unsend`, {});
      const body = res?.data || res;
      if (body?.status === 200) {
        setLastSentAt(null);
        setOffset(0);
        setHasMore(true);
        fetchchat();
      } else {
        alert(body?.message || "Could not unsend");
        setLastSentAt(null);
      }
    } catch (e) {
      alert("Could not unsend");
    } finally {
      setUnsendBusy(false);
    }
  };
  const fetchDocument = async (attachmentFile) => {
    try {
      const timestamp = new Date().getTime(); // Generate a unique timestamp
      const response = await api.get(
        `/v1/${practiseid}/messaging/patient/${data.patientid}/attachment/${attachmentFile}?_=${timestamp}`,
        { responseType: "blob" } // Handle binary responses
      );

      if (response) {
        setPreviewUrls((prev) => ({
          ...prev,
          [attachmentFile]: URL.createObjectURL(response),
        }));
      }
    } catch (error) {
      console.error("Error fetching the document:", error);
    }
  };
  const formatDateHeading = (dateStr) => dayjs(dateStr).format("MMMM DD, YYYY");

  let lastDate = "";

  return (
    <div className="bg-white rounded-xl shadow p-3 md:p-4">
      {/* Header */}
      <div className="border border-gray-200 rounded-lg p-3 mb-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
          <div className="flex flex-col items-start">
            <div className="text-[12px] leading-5 uppercase tracking-wide text-[#6B7280] font-inter">
              Appointment(s)/ Purpose
            </div>
            <div className="mt-1 font-medium font-inter text-[#111827] text-[13px]">
              {data.purpose}
            </div>
          </div>

          <div className="flex flex-col items-start">
            <div className="text-[12px] font-inter leading-5 text-[#6B7280]">
              Date & Time
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                <CalendarMonthOutlinedIcon
                  fontSize="small"
                  style={{ color: "#6B7280" }}
                />
                <span className="text-[#374151] font-inter text-[14px]">
                  {data.startdate
                    ? dayjs(data.startdate).format("DD/MM/YYYY")
                    : "-"}
                </span>
              </span>
              <span className="text-gray-400 hidden md:inline">•</span>
              <span className="inline-flex items-center gap-1">
                <AccessTimeOutlinedIcon
                  fontSize="small"
                  style={{ color: "#6B7280" }}
                />
                <span className="text-[#374151] font-inter text-[14px]">
                  {data.starttime
                    ? dayjs(
                        `1970-01-01 ${data.starttime}`,
                        "YYYY-MM-DD HH:mm:ss"
                      ).format("hh:mm A")
                    : "-"}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="border border-gray-200 rounded-xl bg-white">
        <div
          ref={listRef}
          style={{ WebkitOverflowScrolling: "touch" }}
          className="h-[320px] md:h-[420px] max-h-[60vh] overflow-y-auto overscroll-y-contain p-4 pr-3 space-y-3"
          onScroll={handleScroll}
        >
          {loadingMore && (
            <div className="text-center text-sm text-gray-500">
              Loading more messages...
            </div>
          )}
          {chatdata.map((msg, index) => {
            const currentDate = msg.created_on.split("T")[0];
            const showDateHeading = lastDate !== currentDate;
            lastDate = currentDate;

            return (
              <div key={index}>
                {showDateHeading && (
                  <div className="text-[#9CA3AF] font-inter font-normal text-xs text-center mb-2">
                    {formatDateHeading(currentDate)}
                  </div>
                )}

                <div
                  className={`flex ${
                    msg.is_reply === 1 ? "justify-start" : "justify-end"
                  }`}
                >
                  <div
                    className={`relative max-w-[70%] min-w-[20%]  px-2.5 py-1 shadow-sm ${
                      msg.is_reply === 1
                        ? "bg-[#E17954] text-white rounded-t-2xl rounded-r-2xl"
                        : "bg-[#F3F4F6] text-gray-900 rounded-t-2xl rounded-l-2xl"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      {msg.is_reply !== 1 && (
                        <span className="block text-[12px] font-inter font-medium text-[#2563EB] mb-1">
                          {msg.created_by}
                        </span>
                      )}

                      {/* <span className="text-[11px] font-inter text-[#6B7280]">
                        {new Date(msg.created_on).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span> */}
                    </div>

                    {/* <p className={`text-[12px] text-start mt-1 leading-5 font-inter whitespace-pre-line break-words ${
                      msg.is_reply !== 1
                        ? "text-[#1E3A8A]"
                        : "text-[#1F2937]"
                    }`}>
                      {msg.message.replace("(attachment sent)(attachment sent)", "").trim()}
                    </p> */}
                    {msg.attachement ? (
                      previewUrls[msg.attachement] ? (
                        typeof msg.attachement === "string" &&
                        !msg.attachement.endsWith(".pdf") &&
                        !msg.attachement.endsWith(".mp3") ? (
                          <>
                            <a
                              href={previewUrls[msg.attachement]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group"
                            >
                              <img
                                src={previewUrls[msg.attachement]}
                                alt="Attachment Preview"
                                className="h-32 w-32 object-cover rounded-lg mb-2 transition-transform duration-200 ease-in-out group-hover:scale-105"
                              />
                            </a>

                            <div className="text-sm text-gray-800">
                              <p className="truncate">
                                {msg.message
                                  .replace(
                                    "(attachment sent)(attachment sent)",
                                    ""
                                  )
                                  .trim()}
                              </p>
                            </div>
                          </>
                        ) : msg.attachement.endsWith(".mp3") ? (
                          <div className="group">
                            <audio controls className="w-full">
                              <source
                                src={previewUrls[msg.attachement]}
                                type="audio/mp3"
                              />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        ) : (
                          <a
                            href={previewUrls[msg.attachement]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              className="w-6 h-6 text-gray-700"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                              />
                            </svg>

                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-800">
                                {msg.attachement}
                              </span>
                              <span className="text-xs text-blue-500">
                                View Document
                              </span>
                            </div>
                          </a>
                        )
                      ) : (
                        <div>
                          <Skeleton
                            variant="text"
                            width={150}
                            height={20}
                            animation="wave"
                            style={{ display: "inline-block" }}
                          />
                        </div>
                      )
                    ) : msg.msg_tags === "newpt" ||
                      /<[^>]+>/.test(msg.message) ? (
                      <div
                        onClick={(e) => {
                          if (
                            e.target.classList.contains(
                              "sendPatientEnrollmentForm"
                            )
                          ) {
                            // if (!isOnboarding) {
                            //   submitOnBoardingForm({ ph: msg });
                            // }
                          }
                        }}
                        className="text-sm text-gray-800 bg-white"
                        dangerouslySetInnerHTML={{ __html: msg.message }}
                      />
                    ) : (
                      <div className="mt-1">
                        <p
                          className={`text-[12px] font-normal leading-5 text-left font-inter whitespace-pre-line break-words ${
                            msg.is_reply === 1 ? "text-white" : "text-[#1F2937]"
                          }`}
                        >
                          {msg.message
                            .replace("(attachment sent)(attachment sent)", "")
                            .trim()}
                        </p>
                      </div>
                    )}

                    {/* {msg.attachment && (
                      <div className="mt-2">
                        <AttachmentPreview attachment={msg.attachment} />
                      </div>
                    )} */}
                    <span
                      className={`absolute bottom-1 right-2 text-[11px] ${
                        msg.is_reply == 1 ? "text-white/80" : "text-gray-500"
                      }`}
                    >
                      {new Date(msg.created_on).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Composer */}
      <div className="mt-4">
        <div className="rounded-xl border border-gray-200 bg-white flex flex-col">
          <textarea
            className="w-full resize-none outline-none px-4 pt-4 pb-4 text-sm text-gray-900 placeholder-gray-400 rounded-t-xl min-h-[120px]"
            placeholder="Enter message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />

          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-200 rounded-b-xl">
            <label
              htmlFor="fileInput"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer"
            >
              <AttachFileOutlinedIcon style={{ transform: "rotate(60deg)" }} />
            </label>
            {lastSentAt && Date.now() - lastSentAt < 90000 && (
              <button
                type="button"
                onClick={unsendLast}
                disabled={unsendBusy}
                className="inline-flex items-center px-3 py-2 rounded-full border border-red-300 text-red-600 text-[13px] font-inter hover:bg-red-50 disabled:opacity-50"
              >
                {unsendBusy ? "Unsending…" : "Unsend last message"}
              </button>
            )}
            <button
              type="button"
              onClick={sendMessage}
              disabled={chatloader}
              className={`ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-full ${chatloader ? "bg-gray-200 cursor-not-allowed" : "bg-[#E5E7EB]"}`}
            >
              <span className="text-[#9CA3AF] text-[14px] font-inter">
                {chatloader ? "Sending…" : "Send"}
              </span>
              {shareIcon}
            </button>
          </div>
        </div>
      </div>

      {attachment && (
        <div className="flex justify-evenly items-start p-1 mb-2 bg-white fixed bottom-2">
          <AttachmentPreview attachment={attachment} />
          <ClearIcon
            onClick={() => setAttachment(null)}
            className="cursor-pointer"
          />
        </div>
      )}

      <input
        type="file"
        accept=".pdf,.png,.jpeg,.jpg,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.mp4,.avi,.mov,.zip,.rar"
        className="hidden"
        id="fileInput"
        onChange={(e) => {
          if (e.target.files[0]) setAttachment(e.target.files[0]);
          e.target.value = null;
        }}
      />
    </div>
  );
}
